<template>
    <div class="reports" v-loading="loading">
        <el-menu mode="horizontal" class="flex items-center px-30 h-60">
            <h2 class="title">Overview</h2>
            <v-custom-date-range-picker
                @dateRange="
                    (v) => {
                        dateRange = v;
                    }
                "
            ></v-custom-date-range-picker>
        </el-menu>

        <div v-if="reportData" class="p-30">
            <el-row :gutter="20">
                <!-- Order statistics -->
                <el-col
                    :sm="24"
                    :md="12"
                    v-if="reportData.order_details"
                    class="mb-50"
                >
                    <el-row :gutter="10" justify="space-between" class="mb-10">
                        <el-col :sm="12">
                            <h3>Order statistics</h3>
                        </el-col>
                        <el-col :sm="12" class="text-right">
                            <router-link
                                :to="{
                                    name: 'admin',
                                    params: {
                                        model: 'report',
                                        id: 'order_details',
                                        action: 'all',
                                    },
                                }"
                                >Detailed statistics »</router-link
                            >
                        </el-col>
                    </el-row>
                    <el-card
                        shadow="hover"
                        class="cursor-pointer"
                        v-on:click.native="navigateToDetail('order_details')"
                    >
                        <chart-bar-and-line
                            v-bind:data="getOrderDetailsData"
                            v-if="showDetailChart()"
                        ></chart-bar-and-line>
                        <div v-else class="text-gray">No data available.</div>
                    </el-card>
                </el-col>

                <!-- General statistics -->
                <el-col
                    :sm="24"
                    :md="12"
                    v-if="reportData.subscription_data"
                    class="mb-50"
                >
                    <h3 class="mb-10">General statistics</h3>
                    <el-card shadow="none" class="mb-10">
                        <el-row>
                            <el-col :sm="12" class="text-center">
                                <div class="text-big">
                                    {{
                                        reportData.subscription_data.all_orders
                                    }}
                                </div>
                                Orders
                            </el-col>
                            <el-col :sm="12" class="text-center">
                                <div class="text-big">
                                    {{
                                        reportData.subscription_data
                                            .products_sold
                                    }}
                                </div>
                                Products
                            </el-col>
                        </el-row>
                    </el-card>

                    <el-card shadow="none">
                        <el-row
                            :gutter="10"
                            type="flex"
                            align="middle"
                            class="mb-10"
                        >
                            <el-col
                                :sm="24"
                                :md="12"
                                class="text-right text-highlight"
                                >{{
                                    reportData.subscription_data.status
                                }}</el-col
                            >
                            <el-col :sm="24" :md="12">Shop status</el-col>
                        </el-row>
                        <el-row
                            :gutter="10"
                            type="flex"
                            align="middle"
                            class="mb-10"
                        >
                            <el-col
                                :sm="24"
                                :md="12"
                                class="text-right text-highlight"
                                >{{
                                    reportData.subscription_data.products_count
                                }}</el-col
                            >
                            <el-col :sm="24" :md="12">Products in total</el-col>
                        </el-row>
                        <el-row
                            :gutter="10"
                            type="flex"
                            align="middle"
                            class="mb-10"
                        >
                            <el-col
                                :sm="24"
                                :md="12"
                                class="text-right text-highlight"
                                >{{
                                    reportData.subscription_data.total
                                        | currency(
                                            reportData.subscription_data
                                                .currency
                                        )
                                }}</el-col
                            >
                            <el-col :sm="24" :md="12">Total orders</el-col>
                        </el-row>
                        <el-row
                            :gutter="10"
                            type="flex"
                            align="middle"
                            class="mb-10"
                        >
                            <el-col
                                :sm="24"
                                :md="12"
                                class="text-right text-highlight"
                                >{{
                                    reportData.subscription_data.paid_orders
                                        | currency(
                                            reportData.subscription_data
                                                .currency
                                        )
                                }}</el-col
                            >
                            <el-col :sm="24" :md="12">Orders paid</el-col>
                        </el-row>
                        <el-row
                            :gutter="10"
                            type="flex"
                            align="middle"
                            class="mb-10"
                        >
                            <el-col
                                :sm="24"
                                :md="12"
                                class="text-right text-highlight"
                                >{{
                                    (reportData.subscription_data.tickets || 0)
                                        | currency(
                                            reportData.subscription_data
                                                .currency
                                        )
                                }}
                            </el-col>
                            <el-col :sm="24" :md="12">Average total</el-col>
                        </el-row>
                    </el-card>
                </el-col>
            </el-row>

            <!-- Best selling products -->
            <div class="mb-50" v-if="reportData.best_sellers">
                <el-row :gutter="10" justify="space-between" class="mb-10">
                    <el-col :sm="12">
                        <h3>Best selling products</h3>
                    </el-col>
                    <el-col :sm="12" class="text-right">
                        <router-link
                            :to="{
                                name: 'admin',
                                params: {
                                    model: 'report',
                                    id: 'best_seller',
                                    action: 'all',
                                },
                            }"
                            >Detailed statistics »</router-link
                        >
                    </el-col>
                </el-row>

                <el-card
                    shadow="hover"
                    class="cursor-pointer"
                    v-on:click.native="navigateToDetail('best_seller')"
                >
                    <el-row :gutter="10">
                        <el-col :sm="24" :md="12">
                            <chart-pie
                                v-bind:data="getBestsellerData"
                                v-if="getBestsellerData.values.length > 0"
                            ></chart-pie>
                            <div v-else class="text-gray">
                                No data available.
                            </div>
                        </el-col>
                        <el-col :sm="24" :md="12">
                            <table
                                class="w-full"
                                v-if="reportData.best_sellers.length > 0"
                            >
                                <thead>
                                    <tr>
                                        <th class="text-left px-12">Top 5</th>
                                        <th class="text-right px-12">
                                            Quantity
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr
                                        v-for="(
                                            bestseller, i
                                        ) in reportData.best_sellers"
                                        v-if="i < 5"
                                    >
                                        <td>
                                            {{ bestseller.sku }} -
                                            {{ bestseller.name }}
                                        </td>
                                        <td class="text-right">
                                            {{ bestseller.quantity }}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </el-col>
                    </el-row>
                </el-card>
            </div>

            <el-row :gutter="20">
                <!-- New x Existing customers -->
                <el-col
                    :sm="24"
                    :md="12"
                    v-if="reportData.new_vs_recurring_orders"
                    class="mb-50"
                >
                    <el-row :gutter="10" justify="space-between" class="mb-10">
                        <el-col :sm="12">
                            <h3>New x Existing customers</h3>
                        </el-col>
                        <el-col :sm="12" class="text-right">
                            <router-link
                                :to="{
                                    name: 'admin',
                                    params: {
                                        model: 'report',
                                        id: 'returning_visitors',
                                        action: 'all',
                                    },
                                }"
                                >Detailed statistics »</router-link
                            >
                        </el-col>
                    </el-row>
                    <el-card
                        shadow="hover"
                        class="cursor-pointer"
                        v-on:click.native="
                            navigateToDetail('returning_visitors')
                        "
                    >
                        <chart-progress
                            v-bind:value1="
                                reportData.new_vs_recurring_orders[1].orders
                            "
                            v-bind:value2="
                                reportData.new_vs_recurring_orders[0].orders
                            "
                            v-bind:description1="
                                reportData.new_vs_recurring_orders[1].type
                            "
                            v-bind:description2="
                                reportData.new_vs_recurring_orders[0].type
                            "
                        ></chart-progress>
                    </el-card>
                </el-col>

                <!-- Male x Female -->
                <el-col
                    :sm="24"
                    :md="12"
                    v-if="reportData.gender_based_orders"
                    class="mb-50"
                >
                    <el-row :gutter="10" justify="space-between" class="mb-10">
                        <el-col :sm="12">
                            <h3>Male x Female</h3>
                        </el-col>
                        <el-col :sm="12" class="text-right">
                            <router-link
                                :to="{
                                    name: 'admin',
                                    params: {
                                        model: 'report',
                                        id: 'gender',
                                        action: 'all',
                                    },
                                }"
                                >Detailed statistics »</router-link
                            >
                        </el-col>
                    </el-row>
                    <el-card
                        shadow="hover"
                        class="cursor-pointer"
                        v-on:click.native="navigateToDetail('gender')"
                    >
                        <chart-progress
                            v-bind:value1="
                                reportData.gender_based_orders[0]
                                    .number_of_orders
                            "
                            v-bind:value2="
                                reportData.gender_based_orders[1]
                                    .number_of_orders
                            "
                            v-bind:description1="
                                reportData.gender_based_orders[0].gender
                            "
                            v-bind:description2="
                                reportData.gender_based_orders[1].gender
                            "
                        ></chart-progress>
                    </el-card>
                </el-col>
            </el-row>

            <el-row :gutter="20">
                <!-- Aborted x Completed -->
                <el-col
                    :sm="24"
                    :md="12"
                    v-if="reportData.abandoned_vs_completed"
                    class="mb-50"
                >
                    <el-row :gutter="10" justify="space-between" class="mb-10">
                        <el-col :sm="12">
                            <h3>Aborted x Completed</h3>
                        </el-col>
                        <el-col :sm="12" class="text-right">
                            <router-link
                                :to="{
                                    name: 'admin',
                                    params: {
                                        model: 'report',
                                        id: 'abandoned',
                                        action: 'all',
                                    },
                                }"
                                >Detailed statistics »</router-link
                            >
                        </el-col>
                    </el-row>
                    <el-card
                        shadow="hover"
                        class="cursor-pointer"
                        v-on:click.native="navigateToDetail('abandoned')"
                    >
                        <chart-progress
                            v-bind:value1="
                                reportData.abandoned_vs_completed[1]
                                    .orders_number
                            "
                            v-bind:value2="
                                reportData.abandoned_vs_completed[0]
                                    .orders_number
                            "
                            v-bind:description1="
                                reportData.abandoned_vs_completed[1].type
                            "
                            v-bind:description2="
                                reportData.abandoned_vs_completed[0].type
                            "
                        ></chart-progress>
                    </el-card>
                </el-col>

                <!-- Orders without stock -->
                <el-col
                    :sm="24"
                    :md="12"
                    v-if="reportData.get_in_vs_out_of_stock"
                    class="mb-50"
                >
                    <el-row :gutter="10" justify="space-between" class="mb-10">
                        <el-col :sm="12">
                            <h3>Orders without stock</h3>
                        </el-col>
                        <el-col :sm="12" class="text-right"> </el-col>
                    </el-row>
                    <el-card shadow="none">
                        <chart-progress
                            v-bind:value1="
                                reportData.get_in_vs_out_of_stock[1]
                                    .number_of_products
                            "
                            v-bind:value2="
                                reportData.get_in_vs_out_of_stock[0]
                                    .number_of_products
                            "
                            v-bind:description1="
                                reportData.get_in_vs_out_of_stock[1].type
                            "
                            v-bind:description2="
                                reportData.get_in_vs_out_of_stock[0].type
                            "
                        ></chart-progress>
                    </el-card>
                </el-col>
            </el-row>
        </div>
    </div>
</template>

<script>
import api from '../libs/api';
import ChartBarAndLine from './ChartBarAndLine';
import chartData from './../libs/chartData.helper.js';
import ChartPie from './ChartPie';
import moment from 'moment';
import reportDefinition from './../libs/report.definition.js';

export default {
    name: 'reports',
    props: ['config', 'rootConfig'],
    components: { ChartBarAndLine, ChartPie },

    data() {
        return {
            reportData: {},
            loading: false,
            dateRange: [],
        };
    },

    watch: {
        dateRange() {
            if (this.dateRange && this.dateRange.length === 2) {
                this.loadOverview();
            }
        },
    },

    computed: {
        getBestsellerData() {
            const values = this.reportData.best_sellers.map((e) => {
                return e.quantity;
            });
            const labels = this.reportData.best_sellers.map((e) => {
                return e.sku + ' - ' + e.name;
            });
            return { labels, values, isBestseller: 5 };
        },

        getOrderDetailsData() {
            const data = this.reportData.order_details.map((e) => {
                if (e.date) {
                    e.date = moment(String(e.date)).format('D.M.YYYY');
                }
                return e;
            });
            return chartData(
                'total',
                data,
                reportDefinition['order_details'],
                null,
                this.reportData.order_details[0].currency
            );
        },
    },

    methods: {
        loadOverview() {
            this.loading = true;
            const startDate = moment(this.dateRange[0]).toISOString(true);
            const endDate = moment(this.dateRange[1]).toISOString(true);

            const apiResource = this.$resource(
                this.rootConfig.endPoint + '/reports/get_overview',
                {},
                {},
                {
                    headers: this.rootConfig.headers,
                    params: { start_date: startDate, end_date: endDate },
                }
            );

            apiResource
                .get()
                .then(this.setReportData)
                .catch(() => {
                    this.loading = false;
                    api.errorHandler;
                });
        },

        setReportData(res) {
            this.reportData = res.body;
            this.loading = false;
        },

        navigateToDetail(id) {
            this.$router.push({
                name: 'admin',
                params: { model: 'report', id, action: 'all' },
            });
        },

        showDetailChart() {
            const detailData = this.getOrderDetailsData;
            return (
                detailData &&
                detailData.values &&
                detailData.values.length > 0 &&
                detailData.values[0].length > 0 &&
                !detailData.values.every((e) => e.every((d) => d === 0))
            );
        },
    },
};
</script>

<style scoped>
.w-full {
    width: 100%;
}

.h-full {
    height: 100%;
}

.h-60 {
    height: 60px;
}

.p-30 {
    padding: 30px;
}

.px-30 {
    padding-left: 30px;
    padding-right: 30px;
}

.mb-50 {
    margin-bottom: 50px;
}

.mb-10 {
    margin-bottom: 10px;
}

h3 {
    font-size: 1.2rem;
    font-weight: normal;
    margin: 0;
}

.px-12 {
    padding-right: 12px;
    padding-left: 12px;
}

.cursor-pointer {
    cursor: pointer;
}

table tr {
    background-color: #fff;
}

table td {
    border-bottom: 1px solid #ebeef5;
    box-sizing: border-box;
    padding: 12px;
    transition: background-color 0.25s ease;
    vertical-align: middle;
}

table td b {
    text-transform: capitalize;
}

.title {
    font-size: 1.2rem;
    font-weight: normal;
    margin-right: auto;
}

.flex {
    display: flex;
}

.flex-column {
    flex-direction: column;
}

.flex-column .el-button {
    margin-left: 0 !important;
}

.items-center {
    align-items: center;
}

.gap {
    gap: 20px;
}

a {
    color: #ccc;
    font-weight: 400;
    transition: 0.5s;
}

a:hover {
    color: #000;
}

.text-big {
    font-size: 45px;
}

.text-highlight {
    font-size: 20px;
    line-height: 26px;
}

.text-center {
    text-align: center;
}

.text-right {
    text-align: right;
}

.text-left {
    text-align: left;
}

.text-gray {
    color: #ccc;
}
</style>



// WEBPACK FOOTER //
// Reports.vue?7ff90274